import React from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiLock, FiMail, FiPhone } from 'react-icons/fi';

const PrivacyPolicy = () => {
    const sections = [
        {
            title: 'Information We Collect',
            content: [
                'Personal information (name, email address, phone number) when you contact us or submit inquiries',
                'Company information when you request our services',
                'Usage data including browser type, IP address, and pages visited',
                'Cookies and similar tracking technologies to enhance your browsing experience'
            ]
        },
        {
            title: 'How We Use Your Information',
            content: [
                'To provide and maintain our cleaning and maintenance services',
                'To respond to your inquiries and provide customer support',
                'To send you service updates, promotional materials, and newsletters (with your consent)',
                'To improve our website and services based on usage patterns',
                'To comply with legal obligations and protect our legal rights'
            ]
        },
        {
            title: 'Data Protection & Security',
            content: [
                'We implement industry-standard security measures to protect your personal data',
                'All sensitive information is encrypted during transmission using SSL technology',
                'Access to personal data is restricted to authorized personnel only',
                'We regularly review and update our security practices to maintain data integrity'
            ]
        },
        {
            title: 'Third-Party Sharing',
            content: [
                'We do not sell, trade, or rent your personal information to third parties',
                'We may share information with trusted service providers who assist in operating our website',
                'We may disclose information when required by law or to protect our rights',
                'Any third-party partners are required to maintain confidentiality of your information'
            ]
        },
        {
            title: 'Your Rights',
            content: [
                'Right to access and receive a copy of your personal data',
                'Right to request correction of inaccurate personal information',
                'Right to request deletion of your personal data (subject to legal requirements)',
                'Right to opt-out of marketing communications at any time',
                'Right to lodge a complaint with a supervisory authority'
            ]
        },
        {
            title: 'Cookies Policy',
            content: [
                'We use essential cookies to ensure proper website functionality',
                'Analytics cookies help us understand how visitors interact with our website',
                'You can manage cookie preferences through your browser settings',
                'Disabling cookies may affect some features of our website'
            ]
        }
    ];

    return (
        <div className="w-full">
            {/* Hero Banner */}
            <section className="relative py-24 md:py-32">
                <div className="absolute inset-0">
                    <div className="w-full h-full bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800"></div>
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-20 left-20 w-64 h-64 bg-accent-500 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                    </div>
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        className="w-20 h-20 mx-auto mb-6 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center"
                    >
                        <FiShield className="text-accent-400" size={40} />
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
                    >
                        Privacy <span className="text-accent-400">Policy</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-xl text-white/80 max-w-2xl mx-auto"
                    >
                        Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="text-white/60 mt-6 text-sm"
                    >
                        Last updated: January 2026
                    </motion.p>
                </div>
            </section>

            {/* Policy Content */}
            <section className="section-padding bg-white">
                <div className="container-custom max-w-4xl">
                    {/* Introduction */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="bg-gray-50 rounded-2xl p-8 mb-12"
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <FiLock className="text-primary-600" size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800 mb-3">Our Commitment to Privacy</h2>
                                <p className="text-gray-600 leading-relaxed">
                                    Primeforce Services is committed to protecting your privacy and ensuring the security of your personal information.
                                    This Privacy Policy outlines our practices concerning the collection, use, and disclosure of your data when you
                                    use our services or visit our website.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Policy Sections */}
                    <div className="space-y-10">
                        {sections.map((section, index) => (
                            <motion.div
                                key={section.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                className="border-b border-gray-100 pb-10 last:border-0"
                            >
                                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                                    <span className="w-8 h-8 bg-primary-500 text-white rounded-lg flex items-center justify-center text-sm font-bold">
                                        {index + 1}
                                    </span>
                                    {section.title}
                                </h3>
                                <ul className="space-y-3">
                                    {section.content.map((item, itemIndex) => (
                                        <li key={itemIndex} className="flex items-start gap-3 text-gray-600">
                                            <span className="w-1.5 h-1.5 bg-accent-500 rounded-full mt-2.5 flex-shrink-0"></span>
                                            <span className="leading-relaxed">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-12 bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-8 text-white"
                    >
                        <h3 className="text-2xl font-bold mb-4">Questions About Our Privacy Policy?</h3>
                        <p className="text-white/80 mb-6">
                            If you have any questions or concerns about our privacy practices, please don't hesitate to contact us.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a
                                href="mailto:info@primeforceservices.com"
                                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
                            >
                                <FiMail size={20} />
                                <span>info@primeforceservices.com</span>
                            </a>
                            <a
                                href="tel:+1234567890"
                                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
                            >
                                <FiPhone size={20} />
                                <span>+123 456 7890</span>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;
